import React from 'react';
import _ from 'lodash';
import SchemaForm from 'app/components/general/SchemaForm';
import SectionGroup from 'app/components/general/SectionGroup';
import HeaderThin from 'app/components/layout/HeaderThin';
import countries from 'app/utils/static/countries';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Isvg from 'react-inlinesvg';

import * as userActions from 'app/ducks/users/actions';
import * as addressActions from 'app/ducks/addresses/actions';
import * as userSelectors from 'app/ducks/users/selectors';
import * as addressSelectors from 'app/ducks/addresses/selectors';

import ArrowRightIcon from 'app/images/icons/arrow-right.svg';

const formSchema = {
  required: ['firstName', 'lastName', 'email', 'mobile'],
  properties: {
    firstName: {
      type: 'string',
      title: 'First Name',
    },
    lastName: {
      type: 'string',
      title: 'Last Name',
    },
    email: {
      type: 'string',
      title: 'Email',
    },
    mobile: {
      type: 'string',
      title: 'Mobile',
    },
  },
};
const formAddressSchema = {
  required: ['addressLine1', 'city', 'postcode', 'state', 'country'],
  properties: {
    addressLine1: {
      type: 'string',
      title: 'Address Line 1',
    },
    addressLine2: {
      type: 'string',
      title: 'Address Line 2',
    },
    city: {
      type: 'string',
      title: 'City',
    },
    postcode: {
      type: 'string',
      title: 'Postcode',
    },
    state: {
      type: 'string',
      title: 'State',
    },
    country: {
      type: ['object', 'null'],
      title: 'Country',
      options: countries,
      inputType: 'select',
    },
  },
};

const uiSchema = {
  sport: { 'ui:field': 'select' },
  organisationType: { 'ui:field': 'select' },
};
const uiAddressSchema = {
  country: { 'ui:field': 'select' },
};

const selector = (state) => ({
  users: userSelectors.getUsers(state),
  addresses: addressSelectors.getAddresses(state),
  errors: { ...state.users.backendErrors, ...state.addresses.backendErrors },
});

const actionCreators = _.merge(userActions, addressActions);

interface IProfileFormProps {
  appReady: boolean;
  actions: any;
  addresses: any;
  users: any;
  errors: any;
  backendErrors: any;
  match: any;
}

export interface IProfileFormState {
  originalFormData: any;
  formData: any;
  originalFormAddressData: any;
  formAddressData: any;
  changedAddress: boolean;
  changed: boolean;
  saved: boolean;
  errors: any;
}

@(connect(selector, (dispatch) => ({
  actions: bindActionCreators(actionCreators, dispatch),
})) as any)
export default class ProfileForm extends React.Component<
IProfileFormProps,
IProfileFormState
> {
  static defaultProps: IProfileFormProps = {
    appReady: false,
    actions: undefined,
    match: undefined,
    users: undefined,
    addresses: undefined,
    backendErrors: undefined,
    errors: undefined,
  };

  state: IProfileFormState = {
    originalFormData: {},
    formData: {},
    originalFormAddressData: {},
    formAddressData: {},
    changedAddress: false,
    changed: false,
    saved: false,
    errors: {},
  };

  componentDidUpdate = (prevProps): void => {
    if (
      !this.props.users.hasSaved
      && prevProps.users.currentUser
      !== this.props.users.currentUser
      && !_.isEmpty(this.props.users.currentUser.id)
    ) {
      this.props.actions.getUser(
        this.props.users.currentUserId,
      );
    }
  }

  componentDidMount = (): void => {
    if (!_.isEmpty(this.props.users.currentUser.address?.id)) {
      this.props.actions.getAddress(
        this.props.users.currentUser.address.id,
      );
    }
  }

  static getDerivedStateFromProps(nextProps, prevState): any {
    if (!prevState.changed && !prevState.changedAddress) {
      const formData = {
        firstName: nextProps.users.currentFormUser.firstName || '',
        lastName: nextProps.users.currentFormUser.lastName || '',
        email: nextProps.users.currentFormUser.email || '',
        mobile: nextProps.users.currentFormUser.mobile || '',
      };

      const formAddressData = {
        addressLine1: nextProps.addresses.currentFormAddress.addressLine1 || undefined,
        addressLine2: nextProps.addresses.currentFormAddress.addressLine2 || undefined,
        city: nextProps.addresses.currentFormAddress.city || undefined,
        postcode: nextProps.addresses.currentFormAddress.postcode || undefined,
        state: nextProps.addresses.currentFormAddress.state || undefined,
        country: !_.isEmpty(nextProps.addresses.currentFormAddress.country)
          ? nextProps.addresses.currentFormAddress.country
          : { id: 'AUS', name: 'Australia' },
      };
      return {
        ...prevState,
        formData,
        originalFormData: formData,
        formAddressData,
        originalFormAddressData: formAddressData,
        errors: nextProps.users.errors,
      };
    }
    return null;
  }

  isValid = (): boolean => _.isEmpty(this.state.errors) && this.state.changed;

  isValidAddress = (): boolean => _.isEmpty(this.state.errors) && this.state.changedAddress;

  handleChange = (e): void => {
    const changed = this.state.originalFormData !== e.formData;
    this.setState(() => ({
      formData: e.formData,
      errors: [],
      changed,
    }));
  };

  handleAddressChange = (e): void => {
    const changedAddress = this.state.originalFormAddressData !== e.formData;
    this.setState(() => ({
      formAddressData: e.formData,
      errors: [],
      changedAddress,
    }));
  };

  handleError = (errors): void => {
    this.setState({ errors });
  }

  handleSubmit = (e): void => {
    if (this.isValid()) {
      this.props.actions.updateUser({
        id: this.props.users.currentUser.id,
        firstName: this.state.formData.firstName,
        lastName: this.state.formData.lastName,
        email: this.state.formData.email,
        mobile: this.state.formData.mobile,
      });
    }
    this.setState({ changed: false });
  };

  handleAddressSubmit = (e): void => {
    if (this.isValidAddress()) {
      if (_.isEmpty(this.props.users.currentUser.address?.id)) {
        this.props.actions.createAddress({
          addressLine1: this.state.formAddressData.addressLine1,
          addressLine2: this.state.formAddressData.addressLine2,
          city: this.state.formAddressData.city,
          postcode: this.state.formAddressData.postcode,
          state: this.state.formAddressData.state,
          country: {
            id: this.state.formAddressData.country.id,
            name: this.state.formAddressData.country.name,
          },
          user: {
            id: this.props.users.currentUser.id,
          },
        });
      } else {
        this.props.actions.updateAddress({
          id: this.props.users.currentUser.address.id,
          addressLine1: this.state.formAddressData.addressLine1,
          addressLine2: this.state.formAddressData.addressLine2,
          city: this.state.formAddressData.city,
          postcode: this.state.formAddressData.postcode,
          state: this.state.formAddressData.state,
          country: {
            id: this.state.formAddressData.country.id,
            name: this.state.formAddressData.country.name,
          },
          user: {
            id: this.props.users.currentUser.id,
          },
        });
      }
    }
    this.setState({ changedAddress: false });
  };

  render(): React.ReactNode {
    const { isLoading } = this.props.users;

    return (
      <div>
        <div className="row">
          <div className="col">
            <HeaderThin title="Profile" divider />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <SectionGroup
              title="Your Information"
              isLoading={isLoading}
            >
              <SchemaForm
                uiSchema={uiSchema}
                schema={formSchema}
                handleChange={this.handleChange}
                handleError={this.handleError}
                handleSubmit={this.handleSubmit}
                formData={this.state.formData}
                isValid={this.isValid()}
                liveValidate={false}
                isLoading={this.props.users.isSaving}
                hasSaved={this.props.users.hasSaved}
                hasFailed={this.props.users.hasFailed}
                originalFormData={this.state.originalFormData}
              />
            </SectionGroup>
          </div>
        </div>
        <div className="row with-margin-top">
          <div className="col">
            <SectionGroup
              title="Your Address"
              isLoading={isLoading}
            >
              <SchemaForm
                uiSchema={uiAddressSchema}
                schema={formAddressSchema}
                handleChange={this.handleAddressChange}
                handleError={this.handleError}
                handleSubmit={this.handleAddressSubmit}
                formData={this.state.formAddressData}
                isValid={this.isValidAddress()}
                liveValidate={false}
                isLoading={this.props.addresses.isSaving}
                hasSaved={this.props.addresses.hasSaved}
                hasFailed={this.props.addresses.hasFailed}
                originalFormData={this.state.originalFormAddressData}
              />
            </SectionGroup>
          </div>
        </div>
        <div className="row with-margin-top">
          <div className="col">
            <Link className="btn-2 btn-thin d-inline-flex align-content-center" to="/profile/bank">
              Edit bank details
              {' '}
              <Isvg className="right-arrow-icon" src={ArrowRightIcon} />
            </Link>
          </div>
        </div>
      </div>
    );
  }
}
